import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  Link,
  TextField,
  styled,
} from "@mui/material"
import {
  CheckBoxOutlinedBlankIcon,
  CheckBoxOutlinedIcon,
} from "@tc/ui-dfe/icons"
import { ExportCertificateStatus, CoreStatus } from "@tc/ui-dfe/query"
import {
  WinemakingMarketLabels,
  CommodityLabels,
  CertificateLabels,
} from "@tc/ui-dfe/utils/constants"
import React, { useCallback, useEffect, useState, useMemo } from "react"
import { UseRefinementListProps, useRefinementList } from "react-instantsearch"
import { debounce } from "@mui/material/utils"

const icon = <CheckBoxOutlinedBlankIcon fontSize="small" fill={"info"} />
const checkedIcon = <CheckBoxOutlinedIcon fontSize="small" />

export interface OptionNode {
  icon?: React.ReactNode
  label?: string
}

const StyledTextField = styled(TextField)(() => ({
  "& .MuiInputBase-input": {
    paddingLeft: "0px !important", // Add padding to the input field to account for the icon
  },
}))

export interface RefinementListProps extends UseRefinementListProps {
  placeholder?: string
  labelRender?: (label: string) => string
  defaultOptionLabel?: string
  clearFiltersToggle: boolean
  customOption?: (option?: Option) => OptionNode | undefined
  multiple?: boolean
  showCancelledStatus?: boolean
}

export interface Option {
  label: string
  value?: string
  count?: number
  isLink: boolean
}

interface HtmlAttributesProps {
  key: string
}

export interface RefinementListItem {
  label: string | ExportCertificateStatus
  value: string
  count: number
  isRefined: boolean
}

const getOptionLabel = (option: Option) => {
  if (option instanceof Option) return option.label
  if (Array.isArray(option) && option.length > 0) return option[0].label
  return ""
}

export const TypesenseRefineSelect = (props: RefinementListProps) => {
  const refinement = useRefinementList(props)
  const {
    items,
    refine,
    canToggleShowMore,
    isShowingMore,
    toggleShowMore,
    searchForItems,
  } = refinement

  const [options, setOptions] = useState<Option[]>([])
  const [defaultOption, setDefaultOption] = useState<Option[]>([])
  const [allOptions, setAllOptions] = useState<Option[]>([])

  // Only commodities needs client-side search (fixes full-text search)
  // Since Typesense facet search is optimized for word-level prefix matching, not substring matching.
  const useClientSideSearch = props.attribute === "commodities"

  const debouncedSearchForItems = useMemo(
    () => debounce((query: string) => searchForItems?.(query), 300),
    [searchForItems],
  )

  const getLabel = useCallback(
    (item: RefinementListItem) => {
      /**
       * The label and value for status return the same type
       * of ID (label: 'DRAFT', value: 'DRAFT') instead of (label: 'Draft', value: 'DRAFT').
       * Added the fallback of item.label specifically for destination labels.
       * Destination labels return the correct label and value type.
       */
      if (props.attribute === "eligible_markets") {
        return WinemakingMarketLabels[
          item.value as keyof typeof WinemakingMarketLabels
        ]
      }
      if (props.attribute === "commodities") {
        return CommodityLabels[item.value] ?? item.label
      }
      if (props.attribute === "certificate_type") {
        return (
          CertificateLabels[item.value as keyof typeof CertificateLabels] ??
          item.label
        )
      }
      return item.label
    },
    [props.attribute],
  )

  useEffect(() => {
    let initialOptions: Option[] = items.map((item) => ({
      label: getLabel(item),
      value: item.value,
      count: item.count,
      isLink: false,
    }))

    const isStatusAttribute =
      props.attribute === "status_label" || props.attribute === "status"

    if (isStatusAttribute) {
      const seen = new Map<string, Option>()
      initialOptions.forEach((option) => {
        const key = option.value?.toLowerCase() || ""
        if (
          !seen.has(key) ||
          (seen.get(key)?.count || 0) < (option.count || 0)
        ) {
          seen.set(key, option)
        }
      })
      initialOptions = Array.from(seen.values())

      const isCancelledStatusMissing = !initialOptions.some(
        (option) =>
          option.value?.toLowerCase() === CoreStatus.Cancelled.toLowerCase(),
      )

      if (isCancelledStatusMissing && props.showCancelledStatus) {
        const cancelledLabel =
          CoreStatus.Cancelled.charAt(0).toUpperCase() +
          CoreStatus.Cancelled.slice(1).toLowerCase()
        initialOptions.push({
          label: cancelledLabel,
          value: cancelledLabel,
          count: 0,
          isLink: false,
        })
      }
    }

    // For commodities attribute, ensure ALL known commodities are available
    if (props.attribute === "commodities") {
      const existingValues = new Set(initialOptions.map((opt) => opt.value))
      Object.entries(CommodityLabels).forEach(([value, label]) => {
        if (!existingValues.has(value)) {
          initialOptions.push({
            label,
            value,
            count: 0,
            isLink: false,
          })
        }
      })
    }

    if (props.defaultOptionLabel) {
      let count = 0
      items.map((item) => (count += item.count))
      const defaultItem = {
        label: props.defaultOptionLabel,
        value: "-",
        count: count,
        isLink: false,
      }
      if (!defaultOption || (defaultOption || []).length <= 0) {
        setDefaultOption([defaultItem])
      }
      initialOptions = [defaultItem, ...initialOptions]
    }

    const selectItems = items.filter((item) => item.isRefined)

    if (selectItems && selectItems.length > 0) {
      setDefaultOption(
        selectItems.map((selectItem) => ({
          label: getLabel(selectItem),
          value: selectItem.value,
          count: selectItem.count,
          isLink: false,
        })),
      )
    }

    if (canToggleShowMore) {
      initialOptions = [
        ...initialOptions,
        {
          label: `${isShowingMore ? "Show less" : "Show more"}`,
          value: "",
          count: 0,
          isLink: true,
        },
      ]
    }

    setOptions(initialOptions)

    // Store all options on initial load for client-side filtering
    if (initialOptions.length > allOptions.length) {
      setAllOptions(initialOptions)
    }
  }, [
    items,
    props.defaultOptionLabel,
    canToggleShowMore,
    getLabel,
    isShowingMore,
    allOptions.length,
  ])

  useEffect(() => {
    if (!props.defaultOptionLabel) {
      return
    }
    let count = 0
    items.map((item) => (count += item.count))
    const defaultItem = {
      label: props.defaultOptionLabel || "",
      value: "-",
      count: count,
      isLink: false,
    }

    setDefaultOption([defaultItem])
  }, [props.clearFiltersToggle, items, props.defaultOptionLabel])

  const selectChange = (selectItems: Option[] | Option | null) => {
    // Clear existing refinements
    items.forEach((item) => {
      if (item.isRefined) {
        refine(item.value)
      }
    })

    // Handle null case (when all chips are removed)
    if (!selectItems) {
      setDefaultOption([])
      return
    }

    // Handle array of selections
    if (Array.isArray(selectItems)) {
      const newItems = selectItems.map((item: Option) => {
        refineOption(item)
        return item
      })
      setDefaultOption(newItems)
    } else {
      // Handle single selection
      refineOption(selectItems)
      setDefaultOption([selectItems])
    }
  }

  const refineOption = (item: Option) => {
    item.value !== "-" && refine(item.value || item.label)
  }

  const optionItem = (
    style: HtmlAttributesProps,
    option: Option,
    selected?: boolean,
  ) => {
    const { key, ...noneKeyStyle } = style
    if (option.isLink) {
      return (
        <Link href="#" {...style} key={option?.label} onClick={toggleShowMore}>
          {option.label}
        </Link>
      )
    }

    if (props.customOption && !props.multiple) {
      const result = props.customOption(option)
      return (
        <Box key={option?.label} component="li" {...noneKeyStyle}>
          {result?.icon} {result?.label}
        </Box>
      )
    }

    if (props.multiple) {
      return (
        <Box key={option?.label} component="li" {...noneKeyStyle}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option?.label}
        </Box>
      )
    }

    return (
      <Box key={option?.label} component="li" {...noneKeyStyle}>
        {option.label}
      </Box>
    )
  }

  const startAdornment = useCallback(
    (params: AutocompleteRenderInputParams) => {
      if (!props.multiple && props.customOption) {
        const result = props.customOption(
          defaultOption?.length > 0 ? defaultOption[0] : undefined,
        )
        return (
          <StyledTextField
            {...params}
            sx={{ paddingLeft: 0 }}
            placeholder={props.placeholder}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 2 }}>
                    {params.InputProps.startAdornment}
                    {result && result?.icon}
                  </InputAdornment>
                ),
              },
            }}
          />
        )
      }

      return (
        <TextField
          {...params}
          placeholder={props.placeholder}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
            },
          }}
        />
      )
    },
    [defaultOption],
  )

  return (
    <Autocomplete
      id={`${props.attribute}-refinement-list`}
      options={
        useClientSideSearch
          ? allOptions.length > 0
            ? allOptions
            : options
          : options
      }
      autoHighlight
      data-testid={`${props.attribute}-typesense-refine-select`}
      multiple={props.multiple}
      disableCloseOnSelect={props.multiple}
      isOptionEqualToValue={(option: Option, value) => {
        if (Array.isArray(value)) {
          return !!value.find((v) => v.value === option.value)
        }
        return option.value === value.value
      }}
      value={defaultOption}
      onChange={(_event, item) => selectChange(item)}
      onInputChange={
        useClientSideSearch
          ? undefined
          : (_event, newInputValue, reason) => {
              if (reason === "input") {
                debouncedSearchForItems(newInputValue)
              } else if (reason === "clear") {
                searchForItems("")
              }
            }
      }
      filterOptions={(options, { inputValue }) => {
        // For client-side search (commodities), use substring filtering
        if (useClientSideSearch) {
          if (!inputValue) return options
          const lowerInput = inputValue.toLowerCase()
          return options.filter(
            (option) =>
              !option.isLink &&
              option.label?.toLowerCase().includes(lowerInput),
          )
        }
        // For server-side search (default)
        return options
      }}
      getOptionLabel={getOptionLabel}
      renderOption={(style, option, { selected }) =>
        optionItem(style as HtmlAttributesProps, option, selected)
      }
      renderInput={(params) => startAdornment(params)}
      renderTags={(
        values: Option[],
        getTagProps: AutocompleteRenderGetTagProps,
      ) => {
        return values.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.label}
            label={option.label}
          />
        ))
      }}
    />
  )
}
